import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { apiFetch } from "../utils/apiFetch"
import { useActiveClinic } from "../context/ClinicContext"
import { useAuth } from "../context/AuthContext"
import { useClinicPlanSnapshot } from "../features/subscriptions/hooks/useClinicPlanSnapshot"
import SubscriptionFeatureAccess from "../features/subscriptions/components/SubscriptionFeatureAccess"
import { ui } from "../shared/styles/uiSystem"

const API_BASE =
  (typeof window !== "undefined" &&
  ["localhost", "127.0.0.1", "0.0.0.0"].includes(window.location.hostname)
    ? ["http://localhost", "3000/api"].join(":")
    : "/api")

function getTodayDateInput() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

function normalizeFeatureErrorPayload(rawError) {
  if (!rawError || typeof rawError !== "object") return null

  const payload =
    rawError?.code === "FEATURE_NOT_AVAILABLE"
      ? rawError
      : rawError?.response?.code === "FEATURE_NOT_AVAILABLE"
        ? rawError.response
        : null

  if (!payload) return null

  return {
    code: "FEATURE_NOT_AVAILABLE",
    message:
      payload?.message ||
      "Tu plan actual no incluye esta funcionalidad.",
    missingFeatures: Array.isArray(payload?.missingFeatures)
      ? payload.missingFeatures
      : [],
    recommendedPlans: Array.isArray(payload?.recommendedPlans)
      ? payload.recommendedPlans
      : [],
    currentPlan: payload?.currentPlan || null,
    clinicId: payload?.clinicId || null
  }
}

function extractRecommendedPlan(payload, feature) {
  if (!Array.isArray(payload?.recommendedPlans)) return null
  return payload.recommendedPlans.find((item) => item?.feature === feature)?.recommendedPlan || null
}

async function safeFetchJson(url, fallback) {
  try {
    const res = await apiFetch(url)
    const data = await res.json().catch(() => fallback)

    if (!res.ok) {
      return {
        ok: false,
        data: fallback,
        error: normalizeFeatureErrorPayload(data) || data || null
      }
    }

    return {
      ok: true,
      data: data ?? fallback,
      error: null
    }
  } catch (error) {
    return {
      ok: false,
      data: fallback,
      error: error || null
    }
  }
}

export default function IntelligentCenter() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { activeClinicId, clinicOptions } = useActiveClinic()

  const initialClinicId =
    activeClinicId && activeClinicId !== "ALL"
      ? activeClinicId
      : (localStorage.getItem("odontoflow_clinic_id") || "")

  const [date, setDate] = useState(getTodayDateInput())
  const [clinicId, setClinicId] = useState(
    initialClinicId && initialClinicId !== "ALL" ? initialClinicId : ""
  )

  useEffect(() => {
    if (activeClinicId && activeClinicId !== "ALL") {
      setClinicId(activeClinicId)
    }
  }, [activeClinicId])

  const availableClinics = useMemo(
    () => (clinicOptions || []).filter((c) => String(c.id) !== "ALL"),
    [clinicOptions]
  )

  const { data: planSnapshot } = useClinicPlanSnapshot(clinicId)
  const plan = planSnapshot?.plan || null
  const currentPlanName = plan?.name || "Tu plan actual"
  const canAIByPlan = plan ? Boolean(plan.allowAI) : true

  const [featureLock, setFeatureLock] = useState(null)

  const [preview, setPreview] = useState(null)
  const [applyResult, setApplyResult] = useState(null)
  const [balancer, setBalancer] = useState(null)
  const [futureSaturation, setFutureSaturation] = useState([])
  const [forecast, setForecast] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [actions, setActions] = useState([])
  const [redistribution, setRedistribution] = useState(null)
  const [dailySummary, setDailySummary] = useState(null)
  const [batchResult, setBatchResult] = useState(null)

  const [loadingPreview, setLoadingPreview] = useState(false)
  const [loadingApply, setLoadingApply] = useState(false)
  const [loadingRedistribution, setLoadingRedistribution] = useState(false)
  const [loadingBatch, setLoadingBatch] = useState(false)
  const [loadingPage, setLoadingPage] = useState(false)

  const formatMoney = (value) =>
    new Intl.NumberFormat("es-MX", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(Number(value || 0))

  const effectiveCanAI = canAIByPlan && !featureLock

  const openUpgrade = () => navigate("/upgrade-plan?feature=aiTools")

  const resetData = useCallback(() => {
    setPreview(null)
    setApplyResult(null)
    setBalancer(null)
    setFutureSaturation([])
    setForecast(null)
    setAlerts([])
    setActions([])
    setRedistribution(null)
    setDailySummary(null)
    setBatchResult(null)
  }, [])

  const loadBalancer = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setBalancer(null)
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/balancer?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setBalancer(result.ok ? result.data || null : null)
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setBalancer(null)
    }
  }, [clinicId, date])

  const loadFutureSaturation = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setFutureSaturation([])
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/future-saturation?clinicId=${cId}&date=${d}&days=5`,
        []
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setFutureSaturation(result.ok && Array.isArray(result.data) ? result.data : [])
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setFutureSaturation([])
    }
  }, [clinicId, date])

  const loadForecast = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setForecast(null)
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/income-forecast?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setForecast(result.ok ? result.data || null : null)
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setForecast(null)
    }
  }, [clinicId, date])

  const loadAlerts = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setAlerts([])
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/intelligent-alerts?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setAlerts(Array.isArray(result.data?.alerts) ? result.data.alerts : [])
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setAlerts([])
    }
  }, [clinicId, date])

  const loadRedistribution = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setRedistribution(null)
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/redistribution?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setRedistribution(result.ok ? result.data || null : null)
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setRedistribution(null)
    }
  }, [clinicId, date])

  const loadActionPlan = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setActions([])
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/action-plan?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setActions(Array.isArray(result.data?.actions) ? result.data.actions : [])
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setActions([])
    }
  }, [clinicId, date])

  const loadDailySummary = useCallback(async (cId = clinicId, d = date) => {
    try {
      if (!cId) {
        setDailySummary(null)
        return
      }

      const result = await safeFetchJson(
        `${API_BASE}/appointments/daily-optimization-summary?clinicId=${cId}&date=${d}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        throw featurePayload
      }

      setDailySummary(result.ok ? result.data || null : null)
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)
      if (featurePayload) throw featurePayload
      setDailySummary(null)
    }
  }, [clinicId, date])

  const reloadAll = useCallback(async () => {
    if (!clinicId) {
      resetData()
      return
    }

    if (plan && !canAIByPlan) {
      setFeatureLock({
        message:
          "Tu plan actual no incluye motores IA, automatizaciÃ³n clÃ­nica ni redistribuciÃ³n inteligente.",
        currentPlan: { name: currentPlanName },
        recommendedPlan: null
      })
      resetData()
      return
    }

    setLoadingPage(true)

    try {
      await Promise.all([
        loadBalancer(clinicId, date),
        loadFutureSaturation(clinicId, date),
        loadForecast(clinicId, date),
        loadAlerts(clinicId, date),
        loadRedistribution(clinicId, date),
        loadActionPlan(clinicId, date),
        loadDailySummary(clinicId, date)
      ])

      setFeatureLock(null)
    } catch (error) {
      const featurePayload = normalizeFeatureErrorPayload(error)

      if (featurePayload) {
        setFeatureLock({
          message:
            featurePayload.message ||
            "Tu plan actual no incluye esta funcionalidad.",
          currentPlan: featurePayload.currentPlan || { name: currentPlanName },
          recommendedPlan:
            extractRecommendedPlan(featurePayload, "AI_TOOLS") ||
            extractRecommendedPlan(featurePayload, "AUTOFILL_ENGINE") ||
            extractRecommendedPlan(featurePayload, "REDISTRIBUTION") ||
            null
        })
        resetData()
      } else {
        resetData()
      }
    } finally {
      setLoadingPage(false)
    }
  }, [
    clinicId,
    date,
    plan,
    canAIByPlan,
    currentPlanName,
    resetData,
    loadBalancer,
    loadFutureSaturation,
    loadForecast,
    loadAlerts,
    loadRedistribution,
    loadActionPlan,
    loadDailySummary
  ])

  useEffect(() => {
    if (!clinicId) {
      resetData()
      setFeatureLock(null)
      return
    }

    setPreview(null)
    setApplyResult(null)
    setBatchResult(null)
    reloadAll()
  }, [clinicId, date, reloadAll, resetData])

  const handlePreview = async () => {
    if (!effectiveCanAI) {
      openUpgrade()
      return
    }

    try {
      setLoadingPreview(true)

      const result = await safeFetchJson(
        `${API_BASE}/appointments/auto-fill-v2-preview?clinicId=${clinicId}&date=${date}`,
        null
      )

      const featurePayload = normalizeFeatureErrorPayload(result.error)
      if (featurePayload) {
        setFeatureLock({
          message:
            featurePayload.message ||
            "Tu plan actual no incluye esta funcionalidad.",
          currentPlan: featurePayload.currentPlan || { name: currentPlanName },
          recommendedPlan:
            extractRecommendedPlan(featurePayload, "AI_TOOLS") ||
            extractRecommendedPlan(featurePayload, "AUTOFILL_ENGINE") ||
            null
        })
        resetData()
        return
      }

      setPreview(result.ok ? result.data || null : null)
      setApplyResult(null)
      await reloadAll()
    } catch {
      setPreview({
        totalSuggestions: 0,
        suggestions: [],
        error: "No se pudo cargar la vista previa"
      })
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleApply = async () => {
    if (!effectiveCanAI) {
      openUpgrade()
      return
    }

    try {
      setLoadingApply(true)

      const res = await apiFetch(`${API_BASE}/appointments/auto-fill-v2-apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ clinicId, date })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        const featurePayload = normalizeFeatureErrorPayload(data)
        if (featurePayload) {
          setFeatureLock({
            message:
              featurePayload.message ||
              "Tu plan actual no incluye esta funcionalidad.",
            currentPlan: featurePayload.currentPlan || { name: currentPlanName },
            recommendedPlan:
              extractRecommendedPlan(featurePayload, "AI_TOOLS") ||
              extractRecommendedPlan(featurePayload, "AUTOFILL_ENGINE") ||
              null
          })
          resetData()
          return
        }

        throw new Error(data?.message || "No se pudo aplicar el relleno inteligente")
      }

      setApplyResult(data || null)
      await reloadAll()
    } catch {
      setApplyResult({
        createdCount: 0,
        skippedCount: 0,
        estimatedRevenue: 0,
        created: [],
        skipped: [],
        error: "No se pudo aplicar el relleno inteligente"
      })
    } finally {
      setLoadingApply(false)
    }
  }

  const handleApplyRedistribution = async (appointmentId, toDoctorId) => {
    if (!effectiveCanAI) {
      openUpgrade()
      return
    }

    try {
      setLoadingRedistribution(true)

      const res = await apiFetch(`${API_BASE}/appointments/redistribution/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ appointmentId, toDoctorId })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        const featurePayload = normalizeFeatureErrorPayload(data)
        if (featurePayload) {
          setFeatureLock({
            message:
              featurePayload.message ||
              "Tu plan actual no incluye esta funcionalidad.",
            currentPlan: featurePayload.currentPlan || { name: currentPlanName },
            recommendedPlan:
              extractRecommendedPlan(featurePayload, "AI_TOOLS") ||
              extractRecommendedPlan(featurePayload, "REDISTRIBUTION") ||
              null
          })
          resetData()
          return
        }

        alert(data?.message || "No se pudo aplicar la redistribuciÃ³n")
        return
      }

      alert(data?.message || "RedistribuciÃ³n aplicada")
      await reloadAll()
    } catch {
      alert("Error al aplicar la redistribuciÃ³n")
    } finally {
      setLoadingRedistribution(false)
    }
  }

  const handleApplyAllRedistributions = async () => {
    if (!effectiveCanAI) {
      openUpgrade()
      return
    }

    try {
      const moves = (redistribution?.proposals || []).map((p) => ({
        appointmentId: p.appointmentId,
        toDoctorId: p.toDoctorId
      }))

      if (moves.length === 0) {
        alert("No hay redistribuciones por aplicar")
        return
      }

      setLoadingBatch(true)

      const res = await apiFetch(`${API_BASE}/appointments/redistribution/apply-batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ moves })
      })

      const data = await res.json().catch(() => null)

      if (!res.ok) {
        const featurePayload = normalizeFeatureErrorPayload(data)
        if (featurePayload) {
          setFeatureLock({
            message:
              featurePayload.message ||
              "Tu plan actual no incluye esta funcionalidad.",
            currentPlan: featurePayload.currentPlan || { name: currentPlanName },
            recommendedPlan:
              extractRecommendedPlan(featurePayload, "AI_TOOLS") ||
              extractRecommendedPlan(featurePayload, "REDISTRIBUTION") ||
              null
          })
          resetData()
          return
        }

        throw new Error(data?.message || "No se pudo aplicar la redistribuciÃ³n mÃºltiple")
      }

      setBatchResult(data || null)
      await reloadAll()
    } catch {
      setBatchResult({
        requested: 0,
        appliedCount: 0,
        skippedCount: 0,
        applied: [],
        skipped: []
      })
      alert("Error al aplicar redistribuciÃ³n mÃºltiple")
    } finally {
      setLoadingBatch(false)
    }
  }

  const handleSuggestedAction = async (action) => {
    if (!effectiveCanAI) {
      openUpgrade()
      return
    }

    if (action.type === "APPLY_AUTOFILL") {
      await handleApply()
      return
    }

    if (action.type === "REVIEW_REDISTRIBUTION") {
      alert("Revisa la secciÃ³n de redistribuciÃ³n inteligente mÃ¡s abajo.")
      return
    }

    if (action.type === "PRIORITIZE_UNDERLOADED") {
      alert("Se detectaron doctores subutilizados. Prioriza nuevas citas hacia ellos.")
      return
    }

    if (action.type === "RESOLVE_ALERTS") {
      alert("Revisa primero las alertas automÃ¡ticas y la saturaciÃ³n futura.")
    }
  }

  const kpis = useMemo(() => {
    return {
      sugerenciasActuales: preview?.totalSuggestions || 0,
      creadasUltimaEjecucion: applyResult?.createdCount || 0,
      omitidasUltimaEjecucion: applyResult?.skippedCount || 0,
      revenueUltimaEjecucion: applyResult?.estimatedRevenue || 0,
      ingresoPotencialHoy: forecast?.aiOpportunityRevenue || 0,
      promedioDiario: forecast?.avgDailyRevenue || 0
    }
  }, [preview, applyResult, forecast])

  const getAlertBg = (severity) => {
    if (severity === "HIGH") return "#fee2e2"
    if (severity === "MEDIUM") return "#fef3c7"
    return "#f8fafc"
  }

  const getImpactBg = (label) => {
    if (label === "INGRESO") return "#dcfce7"
    if (label === "RIESGO") return "#fee2e2"
    if (label === "BALANCE") return "#e0f2fe"
    if (label === "CAPACIDAD") return "#fef3c7"
    return "#f8fafc"
  }

  return (
    <div style={ui.page}>
      <div
        style={{
          ...ui.heroCard,
          marginBottom: 18,
          padding: 18,
          background: "linear-gradient(135deg, #0f172a 0%, #0f766e 52%, #14b8a6 100%)"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
            gap: 14,
            alignItems: "center"
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                opacity: 0.84,
                fontWeight: 900,
                letterSpacing: 0.6,
                textTransform: "uppercase"
              }}
            >
              Inteligencia operativa
            </div>

            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 4, lineHeight: 1.02 }}>
              Centro inteligente
            </div>

            <div
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.84)",
                marginTop: 8,
                lineHeight: 1.5
              }}
            >
              Motores IA, automatizaciÃ³n clÃ­nica y acciones operativas sugeridas.
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
              <HeroPill>{clinicId ? "ClÃ­nica activa" : "Sin clÃ­nica"}</HeroPill>
              <HeroPill>{currentPlanName}</HeroPill>
              <HeroPill>{user?.name || "SesiÃ³n activa"}</HeroPill>
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8
            }}
          >
            <KpiCard title="Sugerencias" value={String(kpis.sugerenciasActuales)} />
            <KpiCard title="Creadas" value={String(kpis.creadasUltimaEjecucion)} />
            <KpiCard title="Ingreso IA" value={`$${formatMoney(kpis.ingresoPotencialHoy)}`} />
            <KpiCard title="Promedio diario" value={`$${formatMoney(kpis.promedioDiario)}`} />
          </div>
        </div>
      </div>

      <SectionCard
        title="Contexto de ejecuciÃ³n"
        actions={
          clinicId && effectiveCanAI ? (
            <button onClick={reloadAll} style={ui.secondaryButton}>
              Recargar
            </button>
          ) : null
        }
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 12
          }}
        >
          <Field label="ClÃ­nica">
            <select
              value={clinicId}
              onChange={(e) => setClinicId(e.target.value)}
              style={{
                ...ui.input,
                appearance: "none"
              }}
            >
              <option value="">Selecciona clÃ­nica</option>
              {availableClinics.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Fecha">
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              style={ui.input}
            />
          </Field>
        </div>
      </SectionCard>

      {!clinicId ? (
        <InfoMessage text="Selecciona una clÃ­nica para usar el centro inteligente." />
      ) : !effectiveCanAI ? (
        <div style={{ marginTop: 16 }}>
          <SubscriptionFeatureAccess
            enabled={false}
            featureKey="aiTools"
            planName={currentPlanName}
            title="Centro inteligente disponible en plan superior"
            subtitle="Desbloquea autofill inteligente, redistribuciÃ³n, alertas, forecasting y automatizaciÃ³n clÃ­nica."
            onUpgrade={openUpgrade}
            minHeight={980}
          >
            <IntelligentPreview />
          </SubscriptionFeatureAccess>
        </div>
      ) : (
        <>
          <SectionCard
            title="Motor de relleno inteligente"
            style={{ marginTop: 16 }}
            actions={
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button
                  onClick={handlePreview}
                  disabled={!clinicId || loadingPreview}
                  style={ui.secondaryButton}
                >
                  {loadingPreview ? "Calculando..." : "Calcular oportunidades"}
                </button>

                <button
                  onClick={handleApply}
                  disabled={!clinicId || loadingApply}
                  style={ui.primaryButton}
                >
                  {loadingApply ? "Aplicando..." : "Aplicar oportunidades"}
                </button>
              </div>
            }
          >
            <div style={{ fontSize: 13, color: "#64748b", marginBottom: 14 }}>
              Este bloque calcula oportunidades actuales del motor para la fecha seleccionada.
              Los indicadores de abajo reflejan la ejecuciÃ³n actual o la Ãºltima corrida realizada.
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 14
              }}
            >
              <KpiSoftCard
                title="Sugerencias actuales del motor"
                value={String(kpis.sugerenciasActuales)}
              />

              <KpiSoftCard
                title="Creadas en Ãºltima ejecuciÃ³n"
                value={String(kpis.creadasUltimaEjecucion)}
              />

              <KpiSoftCard
                title="Omitidas en Ãºltima ejecuciÃ³n"
                value={String(kpis.omitidasUltimaEjecucion)}
              />

              <KpiSoftCard
                title="Ingreso de Ãºltima ejecuciÃ³n"
                value={`$${formatMoney(kpis.revenueUltimaEjecucion)}`}
              />

              <KpiSoftCard
                title="Ingreso potencial del dÃ­a"
                value={`$${formatMoney(kpis.ingresoPotencialHoy)}`}
              />

              <KpiSoftCard
                title="Promedio diario histÃ³rico"
                value={`$${formatMoney(kpis.promedioDiario)}`}
              />
            </div>
          </SectionCard>

          <SectionCard title="Resumen de optimizaciÃ³n del dÃ­a" style={{ marginTop: 20 }}>
            {loadingPage ? (
              <EmptyState text="Cargando resumen..." />
            ) : (
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                <div><strong>Autofill:</strong> {dailySummary?.summary?.autofillSuggestions || 0}</div>
                <div><strong>Redistribuciones:</strong> {dailySummary?.summary?.redistributionCandidates || 0}</div>
                <div><strong>Doctores saturados:</strong> {dailySummary?.summary?.overloadedDoctors || 0}</div>
                <div><strong>Doctores con baja carga:</strong> {dailySummary?.summary?.lowLoadDoctors || 0}</div>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Plan de acciÃ³n priorizado" style={{ marginTop: 20 }}>
            {actions.length === 0 ? (
              <EmptyState text="No hay acciones sugeridas para esta fecha." />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {actions.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "center",
                      flexWrap: "wrap"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>
                        {a.title}
                        <span
                          style={{
                            marginLeft: 8,
                            padding: "3px 8px",
                            borderRadius: 999,
                            background: getImpactBg(a.impactLabel),
                            fontSize: 12
                          }}
                        >
                          {a.impactLabel} Â· {a.impactScore}
                        </span>
                      </div>
                      <div style={{ color: "#475569", marginTop: 4 }}>{a.description}</div>
                    </div>

                    <button
                      onClick={() => handleSuggestedAction(a)}
                      style={ui.secondaryButton}
                    >
                      Ejecutar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          <SectionCard title="Alertas automÃ¡ticas" style={{ marginTop: 20 }}>
            {alerts.length === 0 ? (
              <EmptyState text="No hay alertas para esta fecha." />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {alerts.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: getAlertBg(a.severity),
                      border: "1px solid #e5e7eb"
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>{a.title}</div>
                    <div style={{ color: "#475569" }}>{a.message}</div>
                  </div>
                ))}
              </div>
            )}
          </SectionCard>

          {balancer && (
            <SectionCard title="Recomendaciones operativas IA" style={{ marginTop: 20 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 14 }}>
                <div><strong>Sobrecargados:</strong> {balancer.summary?.overloadedDoctors ?? 0}</div>
                <div><strong>Subutilizados:</strong> {balancer.summary?.underloadedDoctors ?? 0}</div>
                <div><strong>Balanceados:</strong> {balancer.summary?.balancedDoctors ?? 0}</div>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                {(balancer.recommendations || []).map((r, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background:
                        r.type === "OVERLOAD"
                          ? "#fee2e2"
                          : r.type === "UNDERLOAD"
                            ? "#e0f2fe"
                            : r.type === "REDISTRIBUTE"
                              ? "#fef3c7"
                              : "#f8fafc",
                      border: "1px solid #e5e7eb",
                      color: "#334155"
                    }}
                  >
                    {r.message}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          <SectionCard title="PredicciÃ³n de saturaciÃ³n futura" style={{ marginTop: 20 }}>
            <div style={{ display: "grid", gap: 10 }}>
              {futureSaturation.length === 0 ? (
                <EmptyState text="Sin predicciÃ³n disponible." />
              ) : (
                futureSaturation.map((f, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background:
                        f.status === "CRITICA"
                          ? "#fee2e2"
                          : f.status === "ALTA"
                            ? "#fef3c7"
                            : f.status === "BAJA"
                              ? "#e0f2fe"
                              : "#f8fafc",
                      border: "1px solid #e5e7eb"
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>{f.date}</div>
                    <div style={{ color: "#475569", marginTop: 4 }}>
                      OcupaciÃ³n promedio: {f.averageOccupationPct}% Â· Estado: {f.status}
                    </div>
                  </div>
                ))
              )}
            </div>
          </SectionCard>

          <SectionCard
            title="RedistribuciÃ³n inteligente"
            style={{ marginTop: 20 }}
            actions={
              <button
                disabled={loadingBatch || !(redistribution?.proposals || []).length}
                onClick={handleApplyAllRedistributions}
                style={ui.secondaryButton}
              >
                {loadingBatch ? "Aplicando..." : "Aplicar todas las redistribuciones"}
              </button>
            }
          >
            {!redistribution || (redistribution.proposals || []).length === 0 ? (
              <EmptyState text="No hay propuestas de redistribuciÃ³n para esta fecha." />
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {redistribution.proposals.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb",
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "center",
                      flexWrap: "wrap"
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>{p.patientName}</div>
                      <div style={{ color: "#475569", marginTop: 4 }}>
                        {p.fromDoctorName} â†’ {p.toDoctorName}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {p.compatibilityReason || "Compatibilidad clÃ­nica vÃ¡lida"}
                      </div>
                      <div style={{ color: "#64748b", fontSize: 13, marginTop: 4 }}>
                        {new Date(p.startTime).toLocaleString("es-MX")}
                      </div>
                    </div>

                    <button
                      disabled={loadingRedistribution}
                      onClick={() => handleApplyRedistribution(p.appointmentId, p.toDoctorId)}
                      style={ui.secondaryButton}
                    >
                      Reasignar cita
                    </button>
                  </div>
                ))}
              </div>
            )}

            {batchResult && (
              <div
                style={{
                  marginTop: 16,
                  padding: 12,
                  borderRadius: 12,
                  background: "#f8fafc",
                  border: "1px solid #e5e7eb"
                }}
              >
                <div><strong>Solicitadas:</strong> {batchResult.requested || 0}</div>
                <div><strong>Aplicadas:</strong> {batchResult.appliedCount || 0}</div>
                <div><strong>Omitidas:</strong> {batchResult.skippedCount || 0}</div>
              </div>
            )}
          </SectionCard>

          {preview && (
            <SectionCard title="Vista previa de relleno inteligente" style={{ marginTop: 20 }}>
              <div style={{ marginBottom: 12 }}>
                <strong>Sugerencias:</strong> {preview.totalSuggestions || 0}
              </div>

              {preview.error && (
                <div style={{ color: "#dc2626", marginBottom: 8 }}>
                  {preview.error}
                </div>
              )}

              <div style={{ display: "grid", gap: 10 }}>
                {(preview.suggestions || []).map((s, i) => (
                  <div
                    key={i}
                    style={{
                      padding: 12,
                      borderRadius: 12,
                      background: "#f8fafc",
                      border: "1px solid #e5e7eb"
                    }}
                  >
                    <div style={{ fontWeight: 700 }}>
                      {s.patientName} Â· {s.treatment}
                    </div>
                    <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
                      {s.doctorName} Â· ${formatMoney(s.expectedRevenue)} Â· {s.duration || 0} min Â· score {s.score}
                    </div>
                    <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                      {s.compatibilityReason || "Compatibilidad del tratamiento no especificada"}
                    </div>

                    <div style={{ fontSize: 12, color: "#475569", marginTop: 4 }}>
                      Doctor asignado: {s.doctorCompatibilityReason || "Compatibilidad clÃ­nica vÃ¡lida"}
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {applyResult && (
            <SectionCard title="Resultado del relleno inteligente" style={{ marginTop: 20 }}>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12 }}>
                <div><strong>Creadas:</strong> {applyResult.createdCount || 0}</div>
                <div><strong>Omitidas:</strong> {applyResult.skippedCount || 0}</div>
                <div><strong>Revenue estimado:</strong> ${formatMoney(applyResult.estimatedRevenue)}</div>
              </div>

              {applyResult.error && (
                <div style={{ color: "#dc2626", marginBottom: 8 }}>
                  {applyResult.error}
                </div>
              )}

              {(applyResult.created || []).length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Citas creadas</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {applyResult.created.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          padding: 12,
                          borderRadius: 12,
                          background: "#ecfeff",
                          border: "1px solid #bae6fd"
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>
                          {c.patientName} Â· {c.treatment}
                        </div>
                        <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
                          {c.doctorName} Â· ${formatMoney(c.expectedRevenue)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(applyResult.skipped || []).length > 0 && (
                <div>
                  <div style={{ fontWeight: 700, marginBottom: 8 }}>Omitidas</div>
                  <div style={{ display: "grid", gap: 8 }}>
                    {applyResult.skipped.map((s, i) => (
                      <div
                        key={i}
                        style={{
                          padding: 12,
                          borderRadius: 12,
                          background: "#fff7ed",
                          border: "1px solid #fdba74"
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{s.patientName}</div>
                        <div style={{ fontSize: 13, color: "#475569", marginTop: 4 }}>
                          {s.reason}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          )}
        </>
      )}
    </div>
  )
}

function HeroPill({ children }) {
  return (
    <span
      style={{
        padding: "6px 10px",
        borderRadius: 999,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.16)",
        color: "#ffffff",
        fontSize: 11,
        fontWeight: 800,
        backdropFilter: "blur(6px)"
      }}
    >
      {children}
    </span>
  )
}

function KpiCard({ title, value }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "rgba(255,255,255,0.12)",
        border: "1px solid rgba(255,255,255,0.16)",
        backdropFilter: "blur(6px)"
      }}
    >
      <div style={{ fontSize: 11, opacity: 0.78, marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 18, fontWeight: 900 }}>{value}</div>
    </div>
  )
}

function KpiSoftCard({ title, value }) {
  return (
    <div style={{ ...ui.softCard, padding: 12 }}>
      <div style={{ fontSize: 12, color: "#64748b" }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 24, fontWeight: 900, color: "#0f172a" }}>
        {value}
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#64748b",
          fontWeight: 800,
          marginBottom: 6
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

function SectionCard({ title, children, actions = null, style = {} }) {
  return (
    <div style={{ ...ui.card, padding: 14, ...style }}>
      <div
        style={{
          marginBottom: 12,
          display: "flex",
          justifyContent: "space-between",
          gap: 10,
          alignItems: "flex-start",
          flexWrap: "wrap"
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 900,
            color: "#0f172a"
          }}
        >
          {title}
        </div>
        {actions}
      </div>
      {children}
    </div>
  )
}

function EmptyState({ text }) {
  return <div style={{ color: "#64748b", fontSize: 13 }}>{text}</div>
}

function InfoMessage({ text }) {
  return (
    <div
      style={{
        ...ui.infoCard,
        padding: 12,
        marginTop: 16,
        fontWeight: 700
      }}
    >
      {text}
    </div>
  )
}

function IntelligentPreview() {
  return (
    <div style={ui.page}>
      <div
        style={{
          ...ui.heroCard,
          marginBottom: 18,
          padding: 18,
          background: "linear-gradient(135deg, #0f172a 0%, #0f766e 52%, #14b8a6 100%)"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.2fr) minmax(260px, 0.8fr)",
            gap: 14,
            alignItems: "center"
          }}
        >
          <div>
            <div style={{ fontSize: 30, fontWeight: 900, marginTop: 4, lineHeight: 1.02 }}>
              Centro inteligente
            </div>

            <div
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,0.84)",
                marginTop: 8,
                lineHeight: 1.5
              }}
            >
              Motores IA, automatizaciÃ³n clÃ­nica y acciones operativas sugeridas.
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              gap: 8
            }}
          >
            <KpiCard title="Sugerencias" value="14" />
            <KpiCard title="Creadas" value="8" />
            <KpiCard title="Ingreso IA" value="$18,500" />
            <KpiCard title="Promedio diario" value="$24,000" />
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gap: 18 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
            gap: 12
          }}
        >
          <PreviewMetric title="Sugerencias IA" value="14" />
          <PreviewMetric title="Redistribuciones" value="4" />
          <PreviewMetric title="Alertas" value="6" />
          <PreviewMetric title="Ingreso potencial" value="$18,500" />
        </div>

        <div style={{ ...ui.card, padding: 14 }}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Plan de acciÃ³n priorizado</div>
          <PreviewList
            rows={[
              "Aplicar oportunidades en huecos de hoy",
              "Redistribuir agenda saturada de un doctor",
              "Reactivar pacientes con alto potencial"
            ]}
          />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16
          }}
        >
          <div style={{ ...ui.card, padding: 14 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>Alertas automÃ¡ticas</div>
            <PreviewList
              rows={[
                "Riesgo de saturaciÃ³n en dos dÃ­as",
                "Capacidad ociosa en un doctor",
                "Ingreso potencial sin aprovechar"
              ]}
            />
          </div>

          <div style={{ ...ui.card, padding: 14 }}>
            <div style={{ fontWeight: 900, marginBottom: 10 }}>RedistribuciÃ³n inteligente</div>
            <PreviewList
              rows={[
                "Paciente A â†’ Doctor 2",
                "Paciente B â†’ Doctor 1",
                "Paciente C â†’ Doctor 3"
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function PreviewMetric({ title, value }) {
  return (
    <div style={{ ...ui.softCard, padding: 12 }}>
      <div style={{ fontSize: 12, color: "#64748b" }}>{title}</div>
      <div style={{ marginTop: 6, fontWeight: 900, fontSize: 22 }}>{value}</div>
    </div>
  )
}

function PreviewList({ rows }) {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {rows.map((row, index) => (
        <div
          key={index}
          style={{
            ...ui.softCard,
            padding: 12,
            fontSize: 13,
            color: "#334155"
          }}
        >
          {row}
        </div>
      ))}
    </div>
  )
}
